// src/api/mockClient.js
//
// Implementa la stessa interfaccia di client.js (get/post/patch) ma risponde
// interamente in memoria, senza rete. Usato quando VITE_KM_MOCK_MODE=true.
// Il flusso Self-Assessment usa le domande REALI dei 5 profili (fixtures
// generate dal file Excel v1.3), incluso il rispetto dei salti condizionali
// (V21) su base testuale ("Solo se Q-XXX = sì").

import sa1 from './fixtures/questionari/SA-1.json';
import sa2a from './fixtures/questionari/SA-2a.json';
import sa2b from './fixtures/questionari/SA-2b.json';
import sa2c from './fixtures/questionari/SA-2c.json';
import sa4 from './fixtures/questionari/SA-4.json';
import { mockEsperienze, mockSchede, mockSearchIndex, mockTemplate } from './mockData.js';

/* ═══════════════════════════════════════════════════════════
   Adattatori verso il contratto reale (Collaudo_KM_TalentGPT_20260907_2.
   docx, §6.9). I dati grezzi in mockData.js restano nella forma comoda
   di prima (denominazione_progetto, cliente_finale, version...) — solo
   qui, al confine con l'esterno, si traducono nella forma che il
   backend vero usa davvero (esperienze[]/risultati[], se_0NN_*,
   marcata_utilizzabile, versione...). Senza questo, il codice delle
   pagine (corretto sul reale) avrebbe visto liste sempre vuote e campi
   vuoti anche in demo. */

function formatPeriodo(e) {
  const fmt = (iso) => {
    if (!iso) return null;
    const [y, m] = iso.split('-');
    return `${m}/${y}`;
  };
  const inizio = fmt(e.data_inizio);
  const fine = e.stato === 'E2' ? fmt(e.data_fine_effettiva) : 'in corso';
  return inizio ? `${inizio} - ${fine}` : null;
}

function toEsperienzaListItem(e) {
  const scheda = e.scheda_id ? mockSchede[e.scheda_id] : null;
  return {
    esperienza_id: e.esperienza_id,
    cliente: e.cliente_finale,
    ruolo: e.ruolo,
    periodo: formatPeriodo(e),
    stato: e.stato === 'E2' ? 'chiusa' : 'aperta',
    sommario: scheda?.sommario || '',
    stato_validazione: 'validato',
  };
}

function toEsperienzaDetail(e) {
  const scheda = e.scheda_id ? mockSchede[e.scheda_id] : null;
  return {
    esperienza_id: e.esperienza_id,
    scheda_id: e.scheda_id || null,
    consulente_id: e.consulente_id,
    data_inizio: e.data_inizio,
    se_002_cliente: e.cliente_finale,
    se_003_settore: e.settore_l1,
    se_004_dominio: e.dominio_l1,
    se_005_contesto_contrattuale: e.contesto_contrattuale || '',
    se_006_ruolo: e.ruolo,
    se_007_periodo: formatPeriodo(e),
    se_008_tecnologie: e.tecnologie || [],
    se_009_presenza_isa: true,
    se_010_citabilita: 'anonimizzata',
    se_011_stato: e.stato === 'E2' ? 'chiusa' : 'aperta',
    se_012_ultima_compilazione: scheda?.ultima_compilazione || e.data_inizio,
    se_013_sommario: scheda?.sommario || '',
    se_016_utilizzabile: scheda?.utilizzabile ?? false,
    se_017_nota_sales: scheda?.marcatura_nota || '',
  };
}

function findEsperienzaBySchedaId(schedaId) {
  return mockEsperienze.find((e) => e.scheda_id === schedaId) || null;
}

function toSchedaDetail(scheda) {
  const esp = findEsperienzaBySchedaId(scheda.scheda_id);
  return {
    id: scheda.scheda_id,
    esperienza_id: esp?.esperienza_id || null,
    versione: scheda.version,
    sommario: scheda.sommario,
    blocco_a: '',
    blocco_b: '',
    blocco_c: '',
    nucleo_comune: '',
    stato_validazione: 'validato',
    fonte: 'self_assessment',
    tecnologie_unificate: esp?.tecnologie || [],
    presenza_isa: true,
    citabilita: 'anonimizzata',
    marcata_utilizzabile: scheda.utilizzabile,
    nota_sales: scheda.marcatura_nota || '',
    sommario_stato: 'pubblicato',
    ultima_compilazione_ts: scheda.ultima_compilazione,
    ultima_compilazione_tipo: scheda.compilazioni?.[0]?.tipo || null,
    aggiornato_il: scheda.ultima_compilazione,
    created_at: scheda.ultima_compilazione,
    consulente_id: esp?.consulente_id || null,
    ruolo: esp?.ruolo || null,
    stato_esperienza: esp ? (esp.stato === 'E2' ? 'chiusa' : 'aperta') : null,
  };
}

function toSearchResult(r, index) {
  const esp = findEsperienzaBySchedaId(r.scheda_id);
  return {
    id: `demo-risultato-${index}`,
    contenuto: r.estratto,
    esperienza_id: esp?.esperienza_id || null,
    titolo: r.titolo,
    settore: esp?.settore_l1 || null,
    dominio: esp?.dominio_l1 || null,
    ruolo: esp?.ruolo || null,
    score: 0.5,
    corpus: 'schede',
    rrf_score: 0.01,
    canali: ['vector'],
  };
}

const PROFILES = { 'sa1': sa1, 'sa2a': sa2a, 'sa2b': sa2b, 'sa2c': sa2c, 'sa4': sa4 };

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeAccents(s) {
  return (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function conditionMet(condizione, answers) {
  if (!condizione) return true;
  const match = condizione.match(/Q-(\d+)\s*=\s*(\S+)/i);
  if (!match) return true;
  const code = `Q-${match[1]}`;
  const expected = normalizeAccents(match[2]);
  const given = normalizeAccents(answers[code] || '');
  return given.startsWith(expected.slice(0, 2)); // "si"/"sì" tollerante
}

function parseQuickReplies(testo) {
  if (/scala da 1 a 5/i.test(testo)) {
    return [1, 2, 3, 4, 5].map((n) => ({ id: String(n), label: String(n) }));
  }
  const parenMatch = testo.match(/\(([^()]+)\)\s*$/);
  if (parenMatch && parenMatch[1].includes('/')) {
    const options = parenMatch[1].split('/').map((s) => s.trim()).filter(Boolean);
    if (options.length >= 2 && options.length <= 6) {
      return options.map((label) => ({ id: label.toLowerCase().replace(/\s+/g, '_'), label }));
    }
  }
  return null;
}

function questionMessage(session) {
  const q = session.questions[session.index];
  const opzioni = parseQuickReplies(q.testo);
  return {
    session_id: session.id,
    text: `${q.sezione}\n\n${q.testo}`,
    // Corretto il 23/09: stessa forma reale confermata nel client vero
    // (18/09) — { type: 'quick_reply', options: [...] }, non
    // { quick_reply: { options: [...] } }. dto.js legge attachment.type,
    // quindi con la forma sbagliata i bottoni non comparivano mai anche
    // nel client finto — bug indipendente dal 500 di rete, non ne era la
    // causa, ma andava comunque corretto.
    attachments: opzioni ? [{ type: 'quick_reply', options: opzioni }] : [],
    closes_session: false,
    // Aggiunto il 23/09, stessa forma di meta.progresso nella risposta
    // reale (orchestrator.py) — così l'indicatore "Domanda X di Y" (Self
    // AssessmentPage.jsx) funziona anche nel selettore demo dell'Hub, non
    // solo con un token vero.
    meta: {
      progresso: {
        domande_attive: session.questions.length,
        risposte_confermate: session.index,
        percentuale: Math.round((100 * session.index) / session.questions.length),
        obbligatorie_mancanti: [],
        completabile: false,
      },
      domanda: q.codice,
    },
  };
}

function advanceToNextEligible(session) {
  session.index += 1;
  while (
    session.index < session.questions.length &&
    !conditionMet(session.questions[session.index].condizione_attivazione, session.answers)
  ) {
    session.index += 1;
  }
}

export function createMockSaEngine() {
  const sessions = new Map();
  let counter = 0;

  function resolveProfile(token) {
    const key = (token || '').toLowerCase().replace(/^demo-?/, '');
    return PROFILES[key] ? key : 'sa1';
  }

  async function open(token) {
    await delay(500);
    const profileKey = resolveProfile(token);
    const fixture = PROFILES[profileKey];
    const id = `demo-session-${++counter}`;
    sessions.set(id, { id, profile: fixture.profilo, questions: fixture.domande, index: -1, answers: {} });

    return {
      session_id: id,
      text: `Ciao! Sono l'assistente ISA per il Self-Assessment (profilo ${fixture.profilo}, ${fixture.n_domande} domande simulate). Iniziamo quando vuoi.`,
      attachments: [],
      closes_session: false,
    };
  }

  async function message(sessionId, text, replyId) {
    await delay(450);
    const session = sessions.get(sessionId);
    if (!session) {
      const err = new Error('Sessione demo non trovata');
      err.status = 403;
      throw err;
    }

    if (session.index === -1) {
      // primo messaggio dopo il consenso
      advanceToNextEligible(session);
      if (session.index >= session.questions.length) {
        return { text: 'Profilo senza domande da somministrare.', closes_session: true };
      }
      return questionMessage(session);
    }

    // registra la risposta alla domanda corrente
    const current = session.questions[session.index];
    session.answers[current.codice] = replyId || text;

    advanceToNextEligible(session);
    if (session.index >= session.questions.length) {
      return {
        text: 'Grazie! Hai completato il Self-Assessment (simulato). Le tue risposte sono state registrate.',
        closes_session: true,
      };
    }
    return questionMessage(session);
  }

  return { open, message };
}

export function createMockKmClient() {
  const saEngine = createMockSaEngine();
  const esperienze = mockEsperienze.map((e) => ({ ...e }));
  const schede = Object.fromEntries(Object.entries(mockSchede).map(([k, v]) => [k, { ...v }]));

  async function get(path, opts = {}) {
    await delay(350);

    if (path === '/esperienze') {
      // Filtro server confermato solo per consulente_id sul reale; le pagine
      // ora filtrano lato client, quindi qui si restituisce sempre tutto.
      return { esperienze: esperienze.map(toEsperienzaListItem), totale: esperienze.length };
    }

    const espMatch = path.match(/^\/esperienze\/([^/]+)$/);
    if (espMatch) {
      const found = esperienze.find((e) => e.esperienza_id === espMatch[1]);
      if (!found) {
        const err = new Error('Non trovata');
        err.status = 404;
        throw err;
      }
      return toEsperienzaDetail(found);
    }

    if (path === '/kl/search') {
      const q = (opts.query?.q || '').toLowerCase();
      const filtered = mockSearchIndex.filter((r) => (r.titolo + ' ' + r.estratto).toLowerCase().includes(q));
      return { risultati: filtered.map(toSearchResult), totale: filtered.length };
    }

    if (path === '/kl/template') {
      // Struttura indicativa, non verificata contro il backend reale (gate G03) —
      // ma nella forma reale (oggetto singolo), non più { sezioni: [...] }.
      return {
        template_id: 'demo-template-1',
        titolo: `${mockTemplate.titolo || 'Modello di case study'} (demo)`,
        tipo: opts.query?.tipo || 'case_study',
        file_path: '/drive/DEMO_case_study.docx',
        istruzioni: "Verificare la citabilità della scheda esperienza di origine prima di usarla. Dato di sviluppo, non verificato contro il reale.",
        versione: 1,
        relevance: 0.5,
      };
    }

    const schedaGetMatch = path.match(/^\/schede\/([^/]+)$/);
    if (schedaGetMatch) {
      const found = schede[schedaGetMatch[1]];
      if (!found) {
        const err = new Error('Non trovata');
        err.status = 404;
        throw err;
      }
      return toSchedaDetail(found);
    }

    throw new Error(`Mock client: GET ${path} non implementato`);
  }

  async function post(path, body, opts = {}) {
    if (path === '/sa/open') {
      return saEngine.open(opts.query?.token);
    }
    if (path === '/sa/message') {
      return saEngine.message(body.session_id, body.text, body.reply_id);
    }

    await delay(500);

    if (path === '/esperienze/open') {
      const id = `exp-demo-${Date.now()}`;
      const created = {
        esperienza_id: id,
        stato: 'E1',
        scheda_id: null,
        ...body,
      };
      esperienze.unshift(created);
      return created;
    }

    if (path === '/kl/ask') {
      await delay(900); // operazione LLM simulata
      return {
        text: `Risposta simulata alla domanda "${body.domanda}": nel Knowledge Layer non ci sono ancora contenuti reali in modalità demo, ma qui comparirebbe la sintesi generata dal backend con i relativi riferimenti.`,
        fonti: mockSearchIndex.slice(0, 2),
      };
    }

    const closeMatch = path.match(/^\/esperienze\/([^/]+)\/close$/);
    if (closeMatch) {
      if (!body?.feedback_performance?.trim() || !body?.feedback_qualita_progetto?.trim()) {
        const err = new Error('Feedback su performance e qualità del progetto sono entrambi obbligatori per chiudere l\'esperienza.');
        err.status = 422;
        throw err;
      }
      const exp = esperienze.find((e) => e.esperienza_id === closeMatch[1]);
      if (exp) {
        exp.stato = 'E2';
        exp.data_fine_effettiva = body.data_fine_effettiva;
        exp.feedback_performance = body.feedback_performance;
        exp.feedback_qualita_progetto = body.feedback_qualita_progetto;
      }
      return exp ? toEsperienzaDetail(exp) : null;
    }

    const markMatch = path.match(/^\/schede\/([^/]+)\/sales-mark$/);
    if (markMatch) {
      const scheda = schede[markMatch[1]];
      if (scheda) {
        scheda.utilizzabile = Boolean(body.utilizzabile);
        scheda.marcatura_nota = body.nota || null;
      }
      return scheda ? toSchedaDetail(scheda) : null;
    }

    const validateMatch = path.match(/^\/schede\/([^/]+)\/validate$/);
    if (validateMatch) {
      return { validated: true };
    }

    throw new Error(`Mock client: POST ${path} non implementato`);
  }

  async function patch(path, body) {
    await delay(400);

    const dateMatch = path.match(/^\/esperienze\/([^/]+)\/data-chiusura-prevista$/);
    if (dateMatch) {
      const exp = esperienze.find((e) => e.esperienza_id === dateMatch[1]);
      if (exp) {
        exp.data_fine_prevista = body.data_chiusura_prevista;
        if (Array.isArray(body.preavvisi_giorni)) exp.preavvisi_giorni = body.preavvisi_giorni;
      }
      return exp ? toEsperienzaDetail(exp) : null;
    }

    const schedaMatch = path.match(/^\/schede\/([^/]+)$/);
    if (schedaMatch) {
      const scheda = schede[schedaMatch[1]];
      if (!scheda) {
        const err = new Error('Versione non più corrente');
        err.status = 409;
        throw err;
      }
      scheda.sommario = body.sommario;
      scheda.version += 1; // internamente resta "version"; esposto come "versione" da toSchedaDetail
      scheda.ultima_compilazione = new Date().toISOString().slice(0, 10);
      return toSchedaDetail(scheda);
    }

    throw new Error(`Mock client: PATCH ${path} non implementato`);
  }

  return { get, post, patch };
}
